import { useState, useEffect } from 'react';

import { cms } from 'lib/cms';

const cmsRequests = {
  drNissa: cms.getDrNissaArticles,
  qa: cms.getQuestionArticles,
};

/**
 * A hook for receiving an expanding array of articles from Contenful
 * using a "Load more" lazy-loading strategy.
 *
 * On each load, more articles would be added to the returned articles.
 *
 * @param {Object} initialEntries - Initial entries
 * @param {('drNissa'|'qa')} requestsType - Which type of articles to get
 * @param {number} articleLimit - The amount of articles to show on each request
 *
 */
export default function useLoadMoreArticles(
  initialEntries,
  requestsType,
  articleLimit = 20
) {
  const [articles, setArticles] = useState(initialEntries.items);
  const [fetchingInProgress, setFetchingInProgress] = useState(false);
  const [reachedArticleLimit, setReachedArticleLimit] = useState(true);

  useEffect(() => {
    // Check if the initial data has reached its limit of articles
    setReachedArticleLimit(initialEntries.items.length >= initialEntries.total);
  }, [initialEntries]);

  async function loadMoreArticles() {
    // Stop if all articles were received
    if (reachedArticleLimit) return;

    setFetchingInProgress(true);

    const cmsRequest = cmsRequests[requestsType];

    const moreResults = await cmsRequest(articleLimit, articles.length);

    const updatedArticles = [...articles, ...moreResults.items];

    setArticles(updatedArticles);
    setReachedArticleLimit(updatedArticles.length >= moreResults.total);
    setFetchingInProgress(false);
  }

  return {
    articles,
    fetchingInProgress,
    loadMoreArticles,
    reachedArticleLimit,
  };
}
